/**
 * Admin API Service
 * 
 * Frontend service layer for calling backend API endpoints.
 * - backend-api: CRUD operations with SurrealDB (templates, players, settings, etc.)
 * - backend-ai: Genkit AI flows (template generation, validation, suggestions)
 */

// Get API base URLs from environment
const FUNCTIONS_BASE = import.meta.env['VITE_FUNCTIONS_URL'] || 'http://127.0.0.1:5001/unwritten-memories/us-east1/';

// Local Fastify backend (for domains API)
const LOCAL_BACKEND_URL = import.meta.env['VITE_BACKEND_URL'] || 'http://localhost:3001';

// Backend API - CRUD operations via SurrealDB (Firebase Functions)
const BACKEND_API_BASE = `${FUNCTIONS_BASE}apiAdmin`;

// Backend AI - Genkit flows for AI operations (Firebase Functions)
const BACKEND_AI_BASE = `${FUNCTIONS_BASE}apiAdminTemplates`;

// Agent API - Agent management (Firebase Functions)
const AGENTS_API_BASE = `${FUNCTIONS_BASE}apiAdminAgents`;

// ============================================================================
// TYPES (matching backend schemas)
// ============================================================================

export interface Template {
  id: string;
  templateId: string;
  name: string;
  json: string;
  tier: string;
  dnaStrand: string;
  status: 'draft' | 'validated' | 'published' | 'archived';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  validationScore?: number;
  tags: string[];
  metadata?: Record<string, unknown>;
}

export interface Player {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  status: 'active' | 'inactive' | 'suspended' | 'banned';
  flags: Array<{
    type: 'warning' | 'review' | 'violation';
    reason: string;
    createdAt: string;
    createdBy: string;
  }>;
  lastActiveAt?: string;
  suspendedAt?: string;
  suspendedBy?: string;
  suspensionReason?: string;
  totalSpent: number;
  aggregatedMetrics: PlayerAggregatedMetrics;
  createdAt: string;
  updatedAt: string;
  characterIds: string[];
  gameState: {
    meters: Record<string, number>;
    traits: Array<any>;
    relationships: Array<any>;
  };
  metadata?: Record<string, unknown>;
}

export interface PlayerAggregatedMetrics {
  charactersCreated: number;
  gamesPlayed: number;
  totalTurns: number;
  novelsGenerated: number;
  totalPlaytimeMs: number;
  genreDistribution: Record<string, number>;
  lastEngagementTrend: 'increasing' | 'stable' | 'declining';
}

export interface PlayerRelationshipGraph {
  nodes: Array<{
    id: string;
    type: 'player' | 'character' | 'game' | 'npc' | 'memory' | 'card' | 'template' | 'world';
    label: string;
    color: string;
    metadata?: Record<string, unknown>;
  }>;
  edges: Array<{
    source: string;
    target: string;
    label: string;
    type: string;
  }>;
}

export interface ValidationResult {
  passed: boolean;
  scores: {
    antiGamification: number;
    design: number;
    gameplay: number;
    monetization: number;
    overall: number;
  };
  report: string;
  criticalIssues: string[];
  recommendations: string[];
  metadata: {
    validatedAt: string;
    validationTimeMs: number;
    cost: number;
    modelUsed: string;
  };
}

export interface EnrichedConcept {
  name: string;
  description: string;
  tier: string;
  dnaStrand: string;
}

export interface TemplateAnalytics {
  templateId: string;
  generationCost: number;
  validationCost: number;
  totalTokens: number;
  thinkingTokens: number;
  usageCount: number;
  performanceMetrics: Record<string, number>;
}

export interface LLMCostStats {
  totalCost: number;
  totalOperations: number;
  byOperationType: Array<{
    operationType: string;
    operations: number;
    totalCost: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalThinkingTokens: number;
  }>;
  byModel: Record<string, { operations: number; cost: number }>;
  daily: Array<{ date: string; operations: number; cost: number }>;
}

export interface TemplateDistributionStats {
  totalTemplates: number;
  byTier: Record<string, number>;
  byDnaStrand: Record<string, number>;
  byStatus: Record<string, number>;
  avgValidationScore: number;
}

// Agent Management Types
export interface AgentMetadata {
  id: string;
  name: string;
  functionName: string;
  type: 'specialist' | 'manager' | 'orchestrator' | 'utility';
  description: string;
  deploymentStatus: 'development' | 'staging' | 'production' | 'deprecated';
  modelConfig?: {
    model: string;
    temperature?: number;
    maxOutputTokens?: number;
    topP?: number;
    topK?: number;
  };
  dependencies: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface AgentPrompt {
  id: string;
  name: string;
  category: string;
  template: string;
  variables: string[];
  usedBy: string[];
  lastModified: string;
  version: number;
}

export interface AgentDeployment {
  id: string;
  agentId: string;
  version: string;
  status: 'pending' | 'success' | 'failed' | 'rollback';
  deployedBy: string;
  deployedAt: string;
  changes: {
    modelConfig: boolean;
    prompts: string[];
    code: boolean;
    functionConfig: boolean;
  };
  logs?: string;
  error?: string;
  metadata?: Record<string, unknown>;
}

export interface AgentGraphNode {
  agent: AgentMetadata;
  stats?: {
    totalCalls: number;
    avgLatency: number;
    errorRate: number;
  };
}

export interface AgentGraphEdge {
  from: string;
  to: string;
  callType: 'direct' | 'conditional' | 'fallback';
  frequency?: number;
}

export interface AgentGraph {
  nodes: AgentGraphNode[];
  edges: AgentGraphEdge[];
  centerNodeId?: string;
}

export interface AgentStats {
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byModel: Record<string, number>;
}

// ============================================================================
// HTTP HELPERS
// ============================================================================

import { appCheckFetch } from '../utils/appCheckFetch';

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  apiBase: string = BACKEND_API_BASE
): Promise<T> {
  const url = `${apiBase}${endpoint}`;
  
  const response = await appCheckFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}

// ============================================================================
// TEMPLATE API
// ============================================================================

export const templateApi = {
  /**
   * List templates with filters and pagination
   */
  list: async (params: {
    page?: number;
    limit?: number;
    tier?: string;
    dnaStrand?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<{
    data: Template[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    
    return request(`/admin/templates?${queryParams.toString()}`, {}, BACKEND_API_BASE);
  },

  /**
   * Get template by ID
   */
  get: async (templateId: string): Promise<Template> => {
    return request(`/admin/templates/${templateId}`, {}, BACKEND_API_BASE);
  },

  /**
   * Create new template
   */
  create: async (template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>): Promise<Template> => {
    return request('/admin/templates', {
      method: 'POST',
      body: JSON.stringify(template),
    }, BACKEND_API_BASE);
  },

  /**
   * Update template
   */
  update: async (templateId: string, updates: Partial<Template>): Promise<Template> => {
    return request(`/admin/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, BACKEND_API_BASE);
  },

  /**
   * Delete template
   */
  delete: async (templateId: string): Promise<void> => {
    await request(`/admin/templates/${templateId}`, {
      method: 'DELETE',
    }, BACKEND_API_BASE);
  },

  /**
   * Publish template
   */
  publish: async (templateId: string): Promise<Template> => {
    return request(`/admin/templates/${templateId}/publish`, {
      method: 'POST',
    }, BACKEND_API_BASE);
  },

  /**
   * Archive template
   */
  archive: async (templateId: string): Promise<Template> => {
    return request(`/admin/templates/${templateId}/archive`, {
      method: 'POST',
    }, BACKEND_API_BASE);
  },

  /**
   * Get validation history for template
   */
  validations: async (templateId: string): Promise<{ validations: ValidationResult[] }> => {
    return request(`/admin/templates/${templateId}/validations`, {}, BACKEND_API_BASE);
  },

  /**
   * Get template analytics
   */
  analytics: async (templateId: string): Promise<TemplateAnalytics> => {
    return request(`/admin/templates/${templateId}/analytics`, {}, BACKEND_API_BASE);
  },
};

// ============================================================================
// TEMPLATE GENERATION API (Genkit Flows)
// ============================================================================

export const generationApi = {
  /**
   * Generate a pack of templates
   */
  generatePack: async (params: {
    concept: string;
    existingTemplates?: Array<{ templateId: string; name: string; tier: string; dnaStrand: string }>;
    targetSetSize?: number;
    tierDistribution?: { tier1Percent: number; tier2Percent: number; tier3Percent: number };
    minDnaStrands?: number;
  }): Promise<{
    templates: any[];
    metadata: {
      totalTemplates: number;
      tierDistribution: Record<string, number>;
      dnaStrandCoverage: string[];
      generationTimeMs: number;
      cost: number;
      modelUsed: string;
    };
  }> => {
    return request('/templates/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    }, BACKEND_AI_BASE);
  },

  /**
   * Generate template scaffolds (concepts)
   */
  generateScaffolds: async (params: {
    theme: string;
    count?: number;
    minDnaStrands?: number;
  }): Promise<{
    concepts: EnrichedConcept[];
    metadata: {
      totalConcepts: number;
      tierDistribution: Record<string, number>;
      dnaStrandCoverage: string[];
      generationTimeMs: number;
      cost: number;
    };
  }> => {
    return request('/templates/scaffold', {
      method: 'POST',
      body: JSON.stringify(params),
    }, BACKEND_AI_BASE);
  },

  /**
   * Promote concept to full template
   */
  promoteConcept: async (params: {
    concept: string;
    tier: string;
    dnaStrand: string;
    description: string;
    existingTemplates?: Array<{ templateId: string; name: string }>;
  }): Promise<{
    template: any;
    metadata: {
      generationTimeMs: number;
      cost: number;
      modelUsed: string;
    };
  }> => {
    return request('/templates/promote', {
      method: 'POST',
      body: JSON.stringify(params),
    }, BACKEND_AI_BASE);
  },

  /**
   * Validate template with 4-specialist committee
   */
  validateTemplate: async (params: {
    templateJson: string;
    templateId: string;
    saveToDatabase?: boolean;
  }): Promise<ValidationResult> => {
    return request('/templates/validate', {
      method: 'POST',
      body: JSON.stringify(params),
    }, BACKEND_AI_BASE);
  },

  /**
   * Get template concept suggestions
   */
  getSuggestions: async (params: {
    existingTemplates?: Array<{ templateId: string; name: string; tier: string; dnaStrand: string; tags?: string[] }>;
    count?: number;
    focusArea?: 'gaps' | 'extensions' | 'variations' | 'any';
  } = {}): Promise<{
    suggestions: string[];
    analysis: {
      identifiedGaps: string[];
      strongPatterns: string[];
      recommendations: string[];
    };
    metadata: {
      generationTimeMs: number;
      cost: number;
      modelUsed: string;
    };
  }> => {
    return request('/templates/suggestions', {
      method: 'POST',
      body: JSON.stringify(params),
    }, BACKEND_AI_BASE);
  },
};

// ============================================================================
// PLAYER API
// ============================================================================

export const playerApi = {
  /**
   * List players with pagination
   */
  list: async (params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    search?: string;
  } = {}): Promise<{
    data: Player[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    
    return request(`/admin/players?${queryParams.toString()}`, {}, BACKEND_API_BASE);
  },

  /**
   * Get player by ID
   */
  get: async (playerId: string): Promise<Player> => {
    return request(`/admin/players/${playerId}`, {}, BACKEND_API_BASE);
  },

  /**
   * Create new player account (Firebase Auth + Firestore)
   */
  create: async (data: {
    email: string;
    displayName: string;
    initialPassword: string;
  }): Promise<Player> => {
    return request(`/admin/players`, {
      method: 'POST',
      body: JSON.stringify(data),
    }, BACKEND_API_BASE);
  },

  /**
   * Suspend player
   */
  suspend: async (playerId: string, reason: string): Promise<Player> => {
    return request(`/admin/players/${playerId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }, BACKEND_API_BASE);
  },

  /**
   * Unsuspend player
   */
  unsuspend: async (playerId: string): Promise<Player> => {
    return request(`/admin/players/${playerId}/unsuspend`, {
      method: 'POST',
    }, BACKEND_API_BASE);
  },

  /**
   * Add flag to player
   */
  addFlag: async (playerId: string, type: 'warning' | 'review' | 'violation', reason: string): Promise<Player> => {
    return request(`/admin/players/${playerId}/flags`, {
      method: 'POST',
      body: JSON.stringify({ type, reason }),
    }, BACKEND_API_BASE);
  },

  /**
   * Remove flag from player
   */
  removeFlag: async (playerId: string, flagIndex: number): Promise<Player> => {
    return request(`/admin/players/${playerId}/flags/${flagIndex}`, {
      method: 'DELETE',
    }, BACKEND_API_BASE);
  },

  /**
   * Get player analytics and aggregated metrics
   */
  getAnalytics: async (playerId: string): Promise<PlayerAggregatedMetrics> => {
    return request(`/admin/players/${playerId}/analytics`, {}, BACKEND_API_BASE);
  },

  /**
   * Get player relationship graph from SurrealDB
   */
  getRelationshipGraph: async (playerId: string): Promise<PlayerRelationshipGraph> => {
    return request(`/admin/players/${playerId}/relationship-graph`, {}, BACKEND_API_BASE);
  },

  /**
   * Reset player game state
   */
  reset: async (playerId: string): Promise<Player> => {
    return request(`/admin/players/${playerId}/reset`, {
      method: 'POST',
    }, BACKEND_API_BASE);
  },
};

// ============================================================================
// ANALYTICS API
// ============================================================================

export const analyticsApi = {
  /**
   * Get template distribution statistics
   */
  distributionStats: async (): Promise<TemplateDistributionStats> => {
    return request('/admin/stats', {}, BACKEND_AI_BASE);
  },

  /**
   * Get LLM cost statistics
   */
  llmCosts: async (params: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  } = {}): Promise<LLMCostStats> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    
    return request(`/admin/costs?${queryParams.toString()}`, {}, BACKEND_AI_BASE);
  },
};

// ============================================================================
// AGENT API
// ============================================================================

export const agentApi = {
  /**
   * List agents with filters and pagination
   */
  list: async (params: {
    page?: number;
    limit?: number;
    type?: string;
    deploymentStatus?: string;
    model?: string;
    search?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<{
    data: AgentMetadata[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'tags') {
          queryParams.append(key, JSON.stringify(value));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });
    
    return request(`/agents?${queryParams.toString()}`, {}, AGENTS_API_BASE);
  },

  /**
   * Get agent by ID
   */
  get: async (agentId: string): Promise<AgentMetadata> => {
    return request(`/agents/${agentId}`, {}, AGENTS_API_BASE);
  },

  /**
   * Create new agent
   */
  create: async (agent: Omit<AgentMetadata, 'id' | 'createdAt' | 'updatedAt'>): Promise<AgentMetadata> => {
    return request('/agents', {
      method: 'POST',
      body: JSON.stringify(agent),
    }, AGENTS_API_BASE);
  },

  /**
   * Update agent
   */
  update: async (agentId: string, updates: Partial<AgentMetadata>): Promise<AgentMetadata> => {
    return request(`/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, AGENTS_API_BASE);
  },

  /**
   * Delete agent
   */
  delete: async (agentId: string): Promise<void> => {
    await request(`/agents/${agentId}`, {
      method: 'DELETE',
    }, AGENTS_API_BASE);
  },

  /**
   * Get agent dependencies
   */
  dependencies: async (agentId: string): Promise<{ dependencies: AgentMetadata[] }> => {
    return request(`/agents/${agentId}/dependencies`, {}, AGENTS_API_BASE);
  },

  /**
   * Get agent dependents
   */
  dependents: async (agentId: string): Promise<{ dependents: AgentMetadata[] }> => {
    return request(`/agents/${agentId}/dependents`, {}, AGENTS_API_BASE);
  },

  /**
   * Test agent
   */
  test: async (agentId: string, input: any): Promise<{
    success: boolean;
    message: string;
    agentId: string;
    input: any;
  }> => {
    return request(`/agents/${agentId}/test`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    }, AGENTS_API_BASE);
  },

  /**
   * Get agent sub-graph
   */
  graph: async (agentId: string, depth: number = 2): Promise<AgentGraph> => {
    return request(`/agents/${agentId}/graph?depth=${depth}`, {}, AGENTS_API_BASE);
  },

  /**
   * Get model config
   */
  getModelConfig: async (agentId: string): Promise<{ modelConfig?: any }> => {
    return request(`/agents/${agentId}/config`, {}, AGENTS_API_BASE);
  },

  /**
   * Update model config
   */
  updateModelConfig: async (agentId: string, modelConfig: any): Promise<AgentMetadata> => {
    return request(`/agents/${agentId}/config`, {
      method: 'PUT',
      body: JSON.stringify({ modelConfig }),
    }, AGENTS_API_BASE);
  },
};

// ============================================================================
// PROMPT API
// ============================================================================

export const promptApi = {
  /**
   * List all prompts
   */
  list: async (): Promise<{ prompts: AgentPrompt[] }> => {
    return request('/prompts', {}, AGENTS_API_BASE);
  },

  /**
   * Get prompt by ID
   */
  get: async (promptId: string): Promise<AgentPrompt> => {
    return request(`/prompts/${promptId}`, {}, AGENTS_API_BASE);
  },

  /**
   * Get prompts for agent
   */
  getByAgent: async (agentId: string): Promise<{ prompts: AgentPrompt[] }> => {
    return request(`/agents/${agentId}/prompts`, {}, AGENTS_API_BASE);
  },

  /**
   * Create new prompt
   */
  create: async (prompt: Omit<AgentPrompt, 'id' | 'lastModified' | 'version'>): Promise<AgentPrompt> => {
    return request('/prompts', {
      method: 'POST',
      body: JSON.stringify(prompt),
    }, AGENTS_API_BASE);
  },

  /**
   * Update prompt
   */
  update: async (promptId: string, updates: Partial<AgentPrompt>): Promise<AgentPrompt> => {
    return request(`/prompts/${promptId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }, AGENTS_API_BASE);
  },

  /**
   * Delete prompt
   */
  delete: async (promptId: string): Promise<void> => {
    await request(`/prompts/${promptId}`, {
      method: 'DELETE',
    }, AGENTS_API_BASE);
  },
};

// ============================================================================
// DEPLOYMENT API
// ============================================================================

export const deploymentApi = {
  /**
   * List deployments
   */
  list: async (agentId?: string): Promise<{ deployments: AgentDeployment[] }> => {
    const queryParams = agentId ? `?agentId=${agentId}` : '';
    return request(`/deployments${queryParams}`, {}, AGENTS_API_BASE);
  },

  /**
   * Get deployment by ID
   */
  get: async (deploymentId: string): Promise<AgentDeployment> => {
    return request(`/deployments/${deploymentId}`, {}, AGENTS_API_BASE);
  },

  /**
   * Create deployment
   */
  create: async (deployment: Omit<AgentDeployment, 'id'>): Promise<AgentDeployment> => {
    return request('/deployments', {
      method: 'POST',
      body: JSON.stringify(deployment),
    }, AGENTS_API_BASE);
  },

  /**
   * Update deployment status
   */
  updateStatus: async (
    deploymentId: string,
    status: 'pending' | 'success' | 'failed' | 'rollback',
    logs?: string,
    error?: string
  ): Promise<AgentDeployment> => {
    return request(`/deployments/${deploymentId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, logs, error }),
    }, AGENTS_API_BASE);
  },
};

// ============================================================================
// GRAPH API
// ============================================================================

export const graphApi = {
  /**
   * Get full dependency graph
   */
  full: async (): Promise<AgentGraph> => {
    return request('/graph', {}, AGENTS_API_BASE);
  },

  /**
   * Get agent statistics
   */
  stats: async (): Promise<AgentStats> => {
    return request('/stats', {}, AGENTS_API_BASE);
  },
};

// ============================================================================
// MONITORING API
// ============================================================================

export interface MetricWithHealth {
  value: number;
  target: number;
  status: 'healthy' | 'warning' | 'critical';
  trend?: 'up' | 'down' | 'stable';
}

export interface OverviewMetrics {
  narrativeUniqueness: MetricWithHealth;
  capacityGates: MetricWithHealth;
  memoryUtilization: MetricWithHealth;
  playerAgency: MetricWithHealth;
  overallHealth: 'healthy' | 'warning' | 'critical';
  timestamp: string;
}

export interface Alert {
  severity: 'critical' | 'warning';
  metric: string;
  message: string;
  value: number;
  target: number;
}

export interface NarrativeQualityMetrics {
  uniquenessScore: number;
  memoryCallbackRate: number;
  narrativeCoherence: number;
  constraintRespectRate: number;
}

export interface UniquenessDistribution {
  score_bucket: number;
  count: number;
}

export interface CallbackData {
  memory_id: string;
  reference_count: number;
}

export interface CoherenceData {
  length_bucket: number;
  avg_coherence: number;
  session_count: number;
}

export interface ConstraintViolation {
  id: string;
  canon_id: string;
  violation_type: string;
  description: string;
  created_at: string;
}

export interface NarrativeBreakdownData {
  summary: NarrativeQualityMetrics;
  uniquenessDistribution: UniquenessDistribution[];
  callbackNetwork: CallbackData[];
  coherenceBySessionLength: CoherenceData[];
  constraintViolations: ConstraintViolation[];
}

export const monitoringApi = {
  /**
   * Get monitoring overview metrics
   */
  getOverview: async (): Promise<OverviewMetrics> => {
    return request('/monitoring/overview', {}, BACKEND_API_BASE);
  },

  /**
   * Get active alerts
   */
  getAlerts: async (): Promise<{ alerts: Alert[] }> => {
    return request('/monitoring/alerts', {}, BACKEND_API_BASE);
  },

  /**
   * Get narrative breakdown analysis
   */
  getNarrativeBreakdown: async (): Promise<NarrativeBreakdownData> => {
    return request('/monitoring/narrative-breakdown', {}, BACKEND_API_BASE);
  },
};

// ============================================================================
// DOMAINS API (Local Fastify Backend)
// ============================================================================

export interface Domain {
  id: string;
  name: string;
  category: string;
  description: string;
  tables?: string[];
  relationships?: Array<{
    to: string;
    via: string;
    type: string;
    cardinality: string;
    description: string;
  }>;
}

export const domainApi = {
  /**
   * List all domains from DOMAIN_CATALOG
   */
  list: async (params: {
    category?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    domains: Domain[];
    total: number;
    limit: number;
    offset: number;
  }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    return request(`/api/domains?${queryParams.toString()}`, {}, LOCAL_BACKEND_URL);
  },

  /**
   * Get domain by ID
   */
  get: async (domainId: string): Promise<Domain> => {
    return request(`/api/domains/${domainId}`, {}, LOCAL_BACKEND_URL);
  },

  /**
   * Get domain graph data
   */
  graph: async (domainId: string): Promise<{
    nodes: Array<{
      id: string;
      label: string;
      type: string;
      category: string;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      label: string;
      type: string;
    }>;
  }> => {
    return request(`/api/domains/${domainId}/graph`, {}, LOCAL_BACKEND_URL);
  },
};

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================

export const adminApi = {
  domains: domainApi,
  templates: templateApi,
  generation: generationApi,
  players: playerApi,
  analytics: analyticsApi,
  agents: agentApi,
  prompts: promptApi,
  deployments: deploymentApi,
  graph: graphApi,
  monitoring: monitoringApi,
};

export default adminApi;

